<template>
  <div class="hierarchy-task" :style="{ marginLeft: `${depth * 20}px` }">
    <div class="task-header">
      <a :href="taskUrl(task.iid)" class="task-title">{{ task.title }}</a>
      <span v-if="task.myTimeSpent > 0" class="task-total-time">{{
        formatDuration(task.myTimeSpent)
      }}</span>
    </div>
    <div v-if="task.timeEntries && task.timeEntries.length > 0" class="task-entries">
      <div v-for="entry in task.timeEntries" :key="entry.id" class="entry-item entry-item-child">
        <div class="entry-main">
          <span class="entry-duration">{{ entry.formattedDuration }}</span>
          <span class="entry-date"
            ><FormattedDateTime
              :value="entry.workedOn + 'T00:00:00'"
              :locale="locale"
              :timezone="timezone"
              format="date"
          /></span>
          <span v-if="isAdminOrOwner && entry.user" class="entry-user"
            >@{{ entry.user.username }}</span
          >
        </div>
        <div v-if="entry.description" class="entry-description">{{ entry.description }}</div>
      </div>
    </div>
    <div v-if="task.childTasks && task.childTasks.length > 0" class="child-tasks">
      <TimeEntriesHierarchy
        v-for="childTask in task.childTasks"
        :key="childTask.id"
        :task="childTask"
        :depth="depth + 1"
        :project-id="projectId"
        :chat-id="chatId"
        :from-id="fromId"
        :locale="locale"
        :timezone="timezone"
        :is-admin-or-owner="isAdminOrOwner"
      />
    </div>
  </div>
</template>

<script setup>
import { s__, __ } from '~/locale'
import FormattedDateTime from '../../common/FormattedDateTime.vue'

const props = defineProps({
  task: { type: Object, required: true },
  depth: { type: Number, default: 0 },
  projectId: { type: [String, Number], required: true },
  chatId: { type: [String, Number], default: null },
  fromId: { type: [String, Number], default: null },
  locale: { type: String, default: null },
  timezone: { type: String, default: null },
  isAdminOrOwner: { type: Boolean, default: false },
})

function taskUrl(taskId) {
  if (!taskId) return '#'
  let url = `/projects/${props.projectId}/tasks/${taskId}`
  const params = []
  if (props.chatId) params.push(`chat_id=${props.chatId}`)
  if (props.fromId) params.push(`from_id=${props.fromId}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
}

function formatDuration(minutes) {
  if (!minutes || minutes === 0) return '0m'
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  const parts = []
  if (hours > 0) parts.push(`${hours}h`)
  if (mins > 0 || parts.length === 0) parts.push(`${mins}m`)
  return parts.join(' ')
}
</script>

<style scoped>
.hierarchy-task {
  margin-bottom: 12px;
  padding-left: 8px;
  border-left: 2px solid #ddd;
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  background: #f5f5f5;
  border-radius: 4px;
  margin-bottom: 6px;
  font-size: 13px;
}

.task-title {
  font-weight: 600;
  color: #2aabee;
  text-decoration: none;
}

.task-title:hover {
  text-decoration: underline;
}

.task-total-time {
  font-weight: 600;
  color: #2aabee;
  font-size: 12px;
}

.task-entries {
  margin-left: 8px;
  margin-top: 4px;
}

.entry-item-child {
  padding: 8px;
  border-bottom: 1px solid #eee;
  position: relative;
  background: #fff;
  border-radius: 3px;
  margin-bottom: 4px;
}

.entry-item-child:last-child {
  border-bottom: none;
}

.entry-main {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.entry-duration {
  font-weight: 600;
  color: #333;
  min-width: 60px;
  font-size: 13px;
}

.entry-date {
  color: #888;
  font-size: 12px;
}

.entry-user {
  color: #2aabee;
  font-size: 12px;
}

.entry-description {
  margin-top: 4px;
  color: #666;
  font-size: 12px;
}

.child-tasks {
  margin-top: 8px;
}
</style>
